import React from 'react';
import { useAdminFetch } from '@/lib/adminApi';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { LogType } from '@/types/logs';

interface Log {
  id: string;
  type: LogType;
  description: string;
  userId: string;
  userName: string;
  ipAddress: string;
  createdAt: string;
}

interface LogsResponse {
  logs: Log[];
  total: number;
}

const LOG_TYPES = [
  'FILE_UPLOAD',
  'FILE_DELETE',
  'FILE_DOWNLOAD',
  'FILE_SHARE',
  'FOLDER_CREATE',
  'FOLDER_DELETE',
  'USER_LOGIN',
  'USER_LOGOUT',
  'USER_CREATE',
  'USER_UPDATE',
  'USER_DELETE',
] as const;

export function ActivitiesPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('tr') ? tr : enUS;
  const [logs, setLogs] = React.useState<Log[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [filters, setFilters] = React.useState({
    type: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  const adminGet = useAdminFetch();

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.type && { type: filters.type }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.search && { search: filters.search }),
      });

      const data = await adminGet<LogsResponse>(`/admin/logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchLogs();
  }, [page, filters, adminGet]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const getLogTypeLabel = (type: string): string => {
    // Log types arrive from the API as free-form strings; the lookup is
    // guarded against the known set, so the dynamic key is safe here.
    const translate = t as (key: string) => string;
    return (LOG_TYPES as readonly string[]).includes(type)
      ? translate(`admin.logTypes.${type}`)
      : type;
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-foreground">{t('admin.activities.title')}</h1>
            <p className="mt-2 text-sm text-foreground">
              {t('admin.activities.subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-foreground">
              {t('admin.activities.type')}
            </label>
            <select
              id="type"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="mt-1 block w-full rounded-md border-border-color shadow-sm focus:border-accent focus:ring-accent sm:text-sm"
            >
              <option value="">{t('admin.activities.all')}</option>
              {LOG_TYPES.map(type => (
                <option key={type} value={type}>
                  {t(`admin.logTypes.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-foreground">
              {t('admin.activities.startDate')}
            </label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="mt-1 block w-full rounded-md border-border-color shadow-sm focus:border-accent focus:ring-accent sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-foreground">
              {t('admin.activities.endDate')}
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="mt-1 block w-full rounded-md border-border-color shadow-sm focus:border-accent focus:ring-accent sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="search" className="block text-sm font-medium text-foreground">
              {t('admin.activities.search')}
            </label>
            <input
              type="text"
              id="search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder={t('admin.activities.searchPlaceholder')}
              className="mt-1 block w-full rounded-md border-border-color shadow-sm focus:border-accent focus:ring-accent sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-border-color md:rounded-lg">
                <table className="min-w-full divide-y divide-border-color">
                  <thead className="bg-background">
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-foreground">
                        {t('admin.activities.datetime')}
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-foreground">
                        {t('admin.activities.user')}
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-foreground">
                        {t('admin.activities.ipAddress')}
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-foreground">
                        {t('admin.activities.action')}
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-foreground">
                        {t('admin.activities.description')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-color bg-card">
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      logs.map(log => (
                        <tr key={log.id}>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">
                            {format(new Date(log.createdAt), 'dd MMM yyyy HH:mm:ss', {
                              locale: dateLocale,
                            })}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">
                            {log.userName}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">
                            {log.ipAddress}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">
                            {getLogTypeLabel(log.type)}
                          </td>
                          <td className="px-3 py-4 text-sm text-muted-foreground">{log.description}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border-color bg-card px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center rounded-md border border-border-color bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                {t('admin.activities.prev')}
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="relative ml-3 inline-flex items-center rounded-md border border-border-color bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                {t('admin.activities.next')}
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-foreground">
                  {t('admin.activities.showing', {
                    from: (page - 1) * 20 + 1,
                    to: Math.min(page * 20, total),
                    total,
                  })}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-muted-foreground ring-1 ring-inset ring-border-color hover:bg-muted focus:z-20 focus:outline-offset-0"
                  >
                    {t('admin.activities.prev')}
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-muted-foreground ring-1 ring-inset ring-border-color hover:bg-muted focus:z-20 focus:outline-offset-0"
                  >
                    {t('admin.activities.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
